import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Mail, Phone, MapPin, Clock, Facebook, MessageCircle } from "lucide-react";
import { BUSINESS } from "@/lib/business";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", form);
      setSent(true);
      toast.success("Message sent. We'll be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send.");
    }
    setSending(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Contact</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Get in touch</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Questions about a product, bulk purchasing, or trade-ins? Send us a message and we'll get back to you.</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-4">
          <InfoCard
            icon={Mail}
            label="Email"
            value={BUSINESS.email}
            href={`mailto:${BUSINESS.email}`}
            testid="contact-info-email"
          />
          <InfoCard
            icon={Phone}
            label="Phone"
            value={BUSINESS.phone}
            href={`tel:${BUSINESS.phoneRaw}`}
            testid="contact-info-phone"
          />
          <InfoCard
            icon={MapPin}
            label="Store location"
            value={BUSINESS.address}
            href={`https://www.google.com/maps/search/?api=1&query=${BUSINESS.mapsQuery}`}
            external
            testid="contact-info-address"
          />
          <InfoCard
            icon={Clock}
            label="Business hours"
            value={BUSINESS.hours}
            testid="contact-info-hours"
          />

          <div className="rounded-2xl bg-slate-900 text-white p-6">
            <div className="text-xs uppercase font-mono text-slate-400 tracking-wider">Reach us on</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={BUSINESS.social.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                href={BUSINESS.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
              >
                <Facebook className="w-4 h-4" /> Facebook
              </a>
            </div>
            <p className="mt-4 text-slate-300 text-xs leading-relaxed">Fastest replies are usually on WhatsApp during business hours.</p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white" data-testid="contact-map">
            <iframe
              title="Store location"
              src={`https://www.google.com/maps?q=${BUSINESS.mapsQuery}&output=embed`}
              className="w-full h-64 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 self-start" data-testid="contact-form">
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <div className="font-display text-xl font-semibold">Thanks — message sent.</div>
              <p className="mt-2 text-slate-600 text-sm">We'll reply within one business day.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <input required data-testid="contact-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              <input required type="email" data-testid="contact-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              <input data-testid="contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              <textarea required rows={6} data-testid="contact-message" placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              <button disabled={sending} data-testid="contact-submit" className="w-full px-5 py-3 rounded-lg bg-slate-900 hover:bg-blue-600 disabled:opacity-60 text-white font-semibold transition-colors">
                {sending ? "Sending…" : "Send message"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, href, external, testid }) {
  const inner = (
    <div className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200 p-5 hover:border-slate-300 transition-colors">
      <span className="w-10 h-10 rounded-lg bg-slate-100 grid place-items-center flex-shrink-0"><Icon className="w-5 h-5 text-slate-900" /></span>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="mt-0.5 text-slate-900 break-words">{value}</div>
      </div>
    </div>
  );
  if (!href) return <div data-testid={testid}>{inner}</div>;
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className="block"
    >
      {inner}
    </a>
  );
}
